import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { AccountShell } from '../../components/account/AccountShell'
import { Button, Text } from '../../components/ui'
import { confirmPayment, getSubscription } from '../../lib/billing-api'
import { authClient } from '../../lib/auth-client'
import { useWorkspaces } from '../../hooks/useWorkspaces'

type CallbackSearch = {
  orderReference?: string
  workspaceId?: string
}

export const Route = createFileRoute('/billing/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    orderReference: typeof search.orderReference === 'string' ? search.orderReference : undefined,
    workspaceId: typeof search.workspaceId === 'string' ? search.workspaceId : undefined,
  }),
  beforeLoad: async () => {
    const { data: session } = await authClient.getSession()
    if (!session) {
      throw redirect({ to: '/login' })
    }
    return { session }
  },
  component: BillingCallbackPage,
})

function BillingCallbackPage() {
  const { orderReference, workspaceId } = Route.useSearch()
  const [pollAttempts, setPollAttempts] = useState(0)
  const [timedOut, setTimedOut] = useState(false)

  const { data: workspacesResult } = useWorkspaces()

  const workspaces = workspacesResult?.success ? workspacesResult.data : []
  const resolvedWorkspaceId =
    workspaceId ?? workspaces.find((w) => w.role === 'admin')?.id ?? null

  const subscriptionQuery = useQuery({
    queryKey: ['billing-subscription-callback', resolvedWorkspaceId, orderReference, pollAttempts],
    enabled: Boolean(resolvedWorkspaceId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'active' || status === 'trialing') return false
      if (pollAttempts >= 30) return false
      return 2000
    },
    queryFn: async () => {
      if (orderReference && resolvedWorkspaceId) {
        const confirmResponse = await confirmPayment(resolvedWorkspaceId, orderReference)
        if (confirmResponse.success) {
          return confirmResponse.data
        }
      }

      const response = await getSubscription(resolvedWorkspaceId!)
      if (!response.success) throw new Error(response.error.message)
      return response.data
    },
  })

  const subscription = subscriptionQuery.data
  const isComplete =
    subscription?.status === 'active' || subscription?.status === 'trialing'

  useEffect(() => {
    if (isComplete) return
    const timer = setInterval(() => {
      setPollAttempts((count) => {
        if (count >= 30) {
          setTimedOut(true)
          clearInterval(timer)
        }
        return count + 1
      })
    }, 2000)
    return () => clearInterval(timer)
  }, [isComplete])

  return (
    <AccountShell>
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-4 py-16 text-center">
        {isComplete ? (
          <>
            <h1 className="text-2xl font-semibold">Payment successful</h1>
            <Text className="text-foreground/60">
              Your workspace is now on the {subscription?.plan} plan
              {subscription?.status === 'trialing' ? ' with a 14-day free trial' : ''}.
            </Text>
            <Link
              to="/billing"
              search={{ workspaceId: resolvedWorkspaceId ?? undefined }}
              className="inline-flex"
            >
              <Button>Back to billing</Button>
            </Link>
          </>
        ) : timedOut ? (
          <>
            <h1 className="text-2xl font-semibold">Still processing</h1>
            <Text className="text-foreground/60">
              We haven&apos;t confirmed your payment yet
              {orderReference ? ` (ref: ${orderReference})` : ''}. Check billing in a few
              minutes or contact support if you were charged.
            </Text>
            <Link
              to="/billing"
              search={{ workspaceId: resolvedWorkspaceId ?? undefined }}
              className="inline-flex"
            >
              <Button>Go to billing</Button>
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">Confirming payment…</h1>
            <Text className="text-foreground/60">
              Please wait while we activate your subscription.
            </Text>
          </>
        )}
      </div>
    </AccountShell>
  )
}
